import type {
  GroupOption,
  Option,
} from "src/components/SelectV2/SelectV2.types.ts";
import type {
  UseFloatingReturn,
  UseInteractionsReturn,
} from "@floating-ui/react";
import React, {
  type MutableRefObject,
  type RefObject,
  useLayoutEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as styles from "src/components/SelectV2/SelectV2.style.ts";
import { InView } from "react-intersection-observer";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { GroupOption as GroupOptionComponent } from "./GroupOption.tsx";
import { Option as OptionComponent } from "./Option.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";

export const Options = <Value = any,>({
  flatOptions,
  options,
  interactions,
  activeIndex,
  selectedIndex,
  onSelect,
  isTypingRef,
  isLoadingData = false,
  searchValue,
  isLoadingMoreOptions,
  onScrollEnd,
  renderOptionEstimateSize,
  listRef,
  floating,
  value,
  isMultiple,
  isOpen,
  pointer,
  onPointerChange,
  listWrapperRef,
}: {
  flatOptions: Option<Value>[];
  options: (Option<Value> | GroupOption<Value>)[];
  interactions: UseInteractionsReturn;
  activeIndex: number | null;
  selectedIndex: number | null;
  searchValue: string | null | undefined;
  onSelect: (item: Option<Value>) => void;
  listWrapperRef: RefObject<HTMLDivElement>;
  isTypingRef: MutableRefObject<boolean>;
  isLoadingData?: boolean;
  isLoadingMoreOptions?: boolean;
  onScrollEnd?: () => void;
  renderOptionEstimateSize?: number;
  listRef: MutableRefObject<Array<HTMLElement | null>>;
  floating: UseFloatingReturn;
  isMultiple: boolean;
  isOpen: boolean;
  value: Value | Value[] | "";
  onPointerChange: (state: boolean) => void;
  pointer: boolean;
}) => {
  const scrollElementRef = useRef(null);
  const { t } = useTranslation(TranslationNamespaceEnum.SelectV2Component);

  if (!isOpen && pointer) {
    onPointerChange(false);
  }

  const estimateSize = (i: number) => {
    if ("options" in options[i]) {
      return (
        options[i].options!.reduce(
          (acc, item) =>
            acc +
            (item.renderOption && renderOptionEstimateSize
              ? renderOptionEstimateSize
              : 35),
          0,
        ) + 35
      );
    } else {
      const item: Option<any> = options[i];
      return item.renderOption && renderOptionEstimateSize
        ? renderOptionEstimateSize
        : 35;
    }
  };

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: estimateSize,
    enabled: true,
    overscan: 5,
  });

  useLayoutEffect(() => {
    if (floating.isPositioned && !pointer) {
      // Nothing has been selected, reset scrolling upon open
      if (activeIndex === null && selectedIndex === null) {
        virtualizer.scrollToIndex(0, {
          behavior: "auto",
        });
      }

      // Scrolling is restored, but the item will be scrolled
      // into view when necessary
      if (activeIndex !== null) {
        // wrapperRef.current?.focus({ preventScroll: true });
        virtualizer.scrollToIndex(activeIndex, {
          behavior: "auto",
        });
      }
    }
  }, [virtualizer, floating.isPositioned, activeIndex, selectedIndex, pointer]);

  useLayoutEffect(() => {
    virtualizer.scrollToIndex(0, {
      behavior: "auto",
    });
  }, [searchValue, virtualizer]);

  const handleInViewChange = (inView: boolean) => {
    if (inView) {
      onScrollEnd?.();
    }
  };

  if (options.length > 0) {
    const virtualItems = virtualizer.getVirtualItems();
    return (
      <div css={styles.list} ref={scrollElementRef}>
        <div
          ref={listWrapperRef}
          tabIndex={0}
          css={styles.optionsContainer({
            height: virtualizer.getTotalSize(),
          })}
        >
          {virtualItems.map((virtualItem) => {
            const option = options[virtualItem.index];
            if ("options" in option) {
              const item: GroupOption<any> = option;
              return (
                <GroupOptionComponent
                  listRef={listRef}
                  virtualItem={virtualItem}
                  key={virtualItem.key}
                  item={item}
                  isMultiple={isMultiple}
                  value={value}
                  onSelect={onSelect}
                  interactions={interactions}
                  activeIndex={activeIndex}
                  flatOptions={flatOptions}
                />
              );
            } else {
              const item = options[virtualItem.index];

              return (
                <OptionComponent
                  listRef={listRef}
                  key={virtualItem.key}
                  flatOptions={flatOptions}
                  virtualItem={virtualItem}
                  item={item}
                  isMultiple={isMultiple}
                  value={value}
                  onSelect={onSelect}
                  interactions={interactions}
                  activeIndex={activeIndex}
                />
              );
            }
          })}
        </div>
        {isLoadingMoreOptions ? (
          <TextV2 variant={"body2"}>{t("loadingMoreOptions")}</TextV2>
        ) : (
          <InView
            style={{
              marginTop: "-1px",
            }}
            as="div"
            onChange={handleInViewChange}
          />
        )}
      </div>
    );
  }

  return <NoResults />;
};

const NoResults = () => {
  const { t } = useTranslation(TranslationNamespaceEnum.SelectV2Component);

  return <div css={styles.noResults}>{t("noOptions")}</div>;
};
